import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { colorOf } from "@/lib/colors";
import { EmployeeAvatar } from "./EmployeeBadge";

const toggleIn = (setter) => (v) => setter((xs) => (xs.includes(v) ? xs.filter((x) => x !== v) : [...xs, v]));

const EmployeePick = ({ employee, active, onClick }) => {
  const c = colorOf(employee);
  return (
    <button
      type="button"
      data-testid={`assign-employee-${employee.id}`}
      aria-pressed={active}
      onClick={onClick}
      className="flex min-h-[48px] items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm font-medium transition-shadow"
      style={{ background: c.bg, borderColor: active ? c.accent : c.border, boxShadow: active ? `0 0 0 2px ${c.accent}` : "none", color: c.ink }}
    >
      <EmployeeAvatar employee={employee} size={26} />
      <span className="min-w-0 flex-1 truncate">{employee.name}</span>
      {active && <Check className="h-4 w-4 shrink-0" />}
    </button>
  );
};

export const AssignDialog = ({ task, employees, days, absenceFor, onClose, onAssign }) => {
  const [empIds, setEmpIds] = useState([]);
  const [dates, setDates] = useState([]);

  useEffect(() => {
    setEmpIds(task?.employee_id ? [task.employee_id] : []);
    setDates([]);
  }, [task]);

  const allAbsent = (iso) => empIds.length > 0 && empIds.every((e) => absenceFor(e, iso));
  const slots = empIds.reduce((n, e) => n + dates.filter((d) => !absenceFor(e, d)).length, 0);
  const verb = task?.date ? "Riprogramma" : "Assegna";
  const confirm = async () => {
    if (await onAssign(task, empIds, dates)) onClose();
  };

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="assign-dialog" className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{task?.date ? "Riprogramma attività" : "Assegna attività"}</DialogTitle>
          <DialogDescription className="truncate">{task?.title}</DialogDescription>
        </DialogHeader>
        {task?.recurring && <p data-testid="assign-recurring-hint" className="rounded-lg bg-violet-50 px-3 py-2 text-xs text-violet-800">Attività ricorrente: resta nell'elenco, nella griglia va una copia.</p>}
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Dipendenti <span className="normal-case tracking-normal text-slate-400">(anche più di uno)</span></p>
        <div className="grid grid-cols-2 gap-2">
          {employees.map((e) => (
            <EmployeePick key={e.id} employee={e} active={empIds.includes(e.id)} onClick={() => toggleIn(setEmpIds)(e.id)} />
          ))}
        </div>
        <p className="mt-2 text-xs font-medium uppercase tracking-wider text-slate-500">Giorni <span className="normal-case tracking-normal text-slate-400">(anche più di uno)</span></p>
        <div className="grid grid-cols-3 gap-2">
          {days.map((d) => {
            const absent = allAbsent(d.iso);
            return (
              <button
                key={d.iso}
                type="button"
                data-testid={`assign-day-${d.idx}`}
                aria-pressed={dates.includes(d.iso)}
                disabled={absent}
                onClick={() => toggleIn(setDates)(d.iso)}
                className={`day-chip w-full disabled:opacity-40 ${dates.includes(d.iso) && !absent ? "is-active" : ""}`}
              >
                <span className="font-display text-sm font-semibold">{d.short}</span>
                <span className="text-[11px] opacity-70">{absent ? "assente" : d.label}</span>
              </button>
            );
          })}
        </div>
        <DialogFooter className="mt-2">
          <Button data-testid="assign-confirm-btn" disabled={slots === 0} onClick={confirm} className="h-11 w-full rounded-xl sm:w-auto">
            {slots > 1 ? `${verb} (${slots})` : verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
